import React, { useState, useEffect, useCallback } from 'react';
import { ToastProvider, useToast } from '../components/Toast';
import ReferralJourney from '../components/ReferralJourney';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');
// Only hides money buttons for non-admins; the server enforces it (requireAdmin
// in referralRoutes.js) — a hidden button is not the control.
const isAdminUser = () => {
  try { return String(JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase() === 'admin'; }
  catch { return false; }
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
function Badge({ label, color, bg }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color, background: bg, display: 'inline-block' }}>
      {label}
    </span>
  );
}

// ─── Pipeline Progress ────────────────────────────────────────────────────────
function Pipeline({ status, darkMode, T }) {
  const steps = ['Lead', 'Task', 'Invoiced', 'Paid', 'Commission'];
  const idx = { lead: 0, task: 1, invoiced: 2, paid: 3, commission_earned: 4 };
  const current = idx[status] ?? 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: i <= current ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3,
              border: `2px solid ${i <= current ? '#1a3a6b' : T.border}`,
              transition: 'all 0.3s',
            }}>
              {i < current ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              ) : (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === current ? '#fff' : T.text3 }} />
              )}
            </div>
            <span style={{ fontSize: 9, color: i <= current ? '#3b82f6' : T.text3, fontWeight: i === current ? 700 : 400, whiteSpace: 'nowrap' }}>{step}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < current ? 'linear-gradient(90deg,#0f1f45,#1a3a6b)' : T.border, minWidth: 20, marginBottom: 16 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Partner Card ─────────────────────────────────────────────────────────────
function PartnerCard({ partner, onView, onDetails, onRedeem, onEncash, darkMode, T }) {
  const isClient = partner.partner_type === 'client';
  const netPayable = isClient ? Math.max(0, (partner.ledger_outstanding || 0) - (partner.credit_balance || 0)) : null;

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden',
      boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)',
      transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = darkMode ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 24px rgba(15,31,69,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)'}>

      {/* Header */}
      <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>
            {(partner.name || 'P').charAt(0).toUpperCase()}
          </div>
          <div>
            <button onClick={() => onDetails && onDetails(partner)} title="View partner details"
              style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.35)' }}>{partner.name}</button>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', margin: '2px 0 0' }}>{partner.referral_partner_id}</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: partner.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.1)', color: partner.status === 'active' ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>
            {partner.status === 'active' ? '● Active' : '○ Inactive'}
          </span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.1)', padding: '2px 7px', borderRadius: 10 }}>
            {isClient ? 'Client Partner' : 'External Partner'}
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>
        {/* Commission rate */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, padding: '8px 12px', background: T.bg3, borderRadius: 8 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          <span style={{ fontSize: 12, color: T.text2, fontWeight: 500 }}>
            Commission: {partner.commission_type === 'percentage' ? `${partner.commission_value}%` : fmt(partner.commission_value)} per referral
          </span>
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isClient ? '1fr 1fr' : '1fr 1fr', gap: 10, marginBottom: 14 }}>

          {/* Ledger Balance — only for client partners */}
          {isClient && (
            <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Ledger Balance</p>
              <p style={{ fontSize: 16, fontWeight: 800, color: (partner.ledger_outstanding || 0) > 0 ? '#dc2626' : '#16a34a', margin: 0 }}>
                {fmt(partner.ledger_outstanding || 0)}
              </p>
              <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>outstanding</p>
            </div>
          )}

          {/* Referral Credits */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Credits Earned</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#16a34a', margin: 0 }}>{fmt(partner.credit_balance || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>available</p>
          </div>

          {/* Total earned */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Total Earned</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#3b82f6', margin: 0 }}>{fmt(partner.total_earned || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>lifetime</p>
          </div>

          {/* Leads count */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Leads Referred</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#8b5cf6', margin: 0 }}>{fmtN(partner.total_leads || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>{partner.converted_leads || 0} converted</p>
          </div>
        </div>

        {/* Net Payable — only for client partners */}
        {isClient && (partner.ledger_outstanding || 0) > 0 && (partner.credit_balance || 0) > 0 && (
          <div style={{ background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.25)' : '#bfdbfe'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>Net Payable (after credit)</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#1d4ed8' }}>{fmt(netPayable)}</span>
            </div>
            <p style={{ fontSize: 11, color: T.text3, margin: '4px 0 0' }}>{fmt(partner.ledger_outstanding)} − {fmt(Math.min(partner.credit_balance, partner.ledger_outstanding))} credit</p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onView(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = T.bg3}
            onMouseLeave={e => e.currentTarget.style.background = T.bg2}>
            View Leads
          </button>
          {isAdminUser() && isClient && (partner.credit_balance || 0) > 0 && (
            <button onClick={() => onRedeem(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Redeem
            </button>
          )}
          {isAdminUser() && (partner.credit_balance || 0) > 0 && (
            <button onClick={() => onEncash(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Encash
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Partner Leads Modal ──────────────────────────────────────────────────────
function PartnerLeadsModal({ partner, onClose, darkMode, T }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${BASE}/api/referral-partners/${partner.id}/leads`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setLeads(Array.isArray(d) ? d : []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, [partner.id, token]);

  const statusColor = (s) => ({
    lead: '#3b82f6', won: '#0ea5e9', task: '#8b5cf6', invoiced: '#f59e0b',
    paid: '#16a34a', commission_earned: '#f97316', completed: '#16a34a',
    lost: '#dc2626', invoice_cancelled: '#d97706',
  }[s] || '#94a3b8');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: 'min(94vw, 820px)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Leads by {partner.name}</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '3px 0 0' }}>{partner.referral_partner_id} · {leads.length} referrals</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, scrollbarWidth: 'none' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: T.text3 }}>Loading...</div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: T.bg3, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              </div>
              <p style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: 0 }}>No referrals yet</p>
              <p style={{ fontSize: 12, color: T.text3, marginTop: 6 }}>Leads referred by this partner will appear here</p>
            </div>
          ) : leads.map((lead, i) => (
            <div key={lead.id} style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>{lead.referred_name || lead.lead_name || lead.contact_person || 'Unknown'}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>Referred {lead.referral_date ? new Date(lead.referral_date).toLocaleDateString('en-IN') : '-'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color: statusColor(lead.status), background: `${statusColor(lead.status)}22` }}>
                    {lead.journey?.label || lead.status?.replace(/_/g, ' ') || 'lead'}
                  </span>
                  {lead.lead_ref && <p style={{ fontSize: 10, color: T.text3, margin: '4px 0 0' }}>{lead.lead_ref}</p>}
                </div>
              </div>
              {lead.journey
                ? <ReferralJourney journey={lead.journey} showConflicts T={{ bg: T.bg2, card: T.bg2, border: T.border, text: T.text, text2: T.text2, text3: T.text3 }} />
                : <Pipeline status={lead.status} darkMode={darkMode} T={T} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Redeem Modal ─────────────────────────────────────────────────────────────
function RedeemModal({ partner, onClose, onSuccess, darkMode, T }) {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${BASE}/api/clients/${partner.client_id}/invoices?status=unpaid`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setInvoices(Array.isArray(d) ? d : []))
      .catch(() => setInvoices([]));
  }, [partner.client_id, token]);

  const handleRedeem = async () => {
    if (!selectedInvoice || !amount) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/${partner.id}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_id: selectedInvoice, amount: Number(amount) })
      });
      onSuccess();
      onClose();
    } catch (_) {}
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#16a34a,#15803d)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Redeem Credits</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>Available: {fmt(partner.credit_balance)}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Select Invoice to Adjust</label>
            <select value={selectedInvoice} onChange={e => setSelectedInvoice(e.target.value)} style={is}>
              <option value="">Select pending invoice...</option>
              {invoices.map(inv => (
                <option key={inv.id} value={inv.id}>{inv.invoice_number} — {fmt(inv.outstanding_amount)} outstanding</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Amount to Redeem</label>
            <input type="number" min={1} max={partner.credit_balance} value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Max ${fmt(partner.credit_balance)}`} style={is} />
          </div>
          {selectedInvoice && amount && (
            <div style={{ background: darkMode ? 'rgba(22,163,74,0.1)' : '#f0fdf4', border: `1px solid ${darkMode ? 'rgba(22,163,74,0.25)' : '#bbf7d0'}`, borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, margin: 0 }}>
                Ledger entry: −{fmt(amount)} credit applied against invoice
              </p>
              <p style={{ fontSize: 11, color: T.text3, margin: '4px 0 0' }}>No Credit Note issued. GST-safe contra settlement.</p>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleRedeem} disabled={saving || !selectedInvoice || !amount} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!selectedInvoice || !amount || saving) ? 0.6 : 1 }}>
            {saving ? 'Processing...' : 'Apply Credit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Encash Modal ─────────────────────────────────────────────────────────────
// ─── Bank account on file ─────────────────────────────────────────────────────
// One account per partner. Saving a change clears "Verified"; payouts only go to
// a verified account (enforced on the server).
function BankModal({ partner, onClose, onSuccess, darkMode, T }) {
  const { showToast } = useToast();
  const token = localStorage.getItem('token');
  const [bank, setBank] = useState(null);
  const [form, setForm] = useState({ bank_account_name: '', bank_name: '', bank_account_number: '', bank_ifsc: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/bank`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setBank(d.bank || null);
      if (d.bank) setForm({ bank_account_name: d.bank.account_name || '', bank_name: d.bank.bank_name || '', bank_account_number: d.bank.account_number || '', bank_ifsc: d.bank.ifsc || '' });
    } catch (_) {}
  }, [partner.id, token]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, body, okMsg) => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/${path}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { showToast(d.message || okMsg, 'success'); await load(); onSuccess && onSuccess(); }
      else showToast(d.message || 'Could not save', 'error');
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' };
  const masked = /^XXXX/.test(form.bank_account_number);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Bank Account on File</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '3px 0 0' }}>{partner.name} · {partner.referral_partner_id}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {bank ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderRadius: 10,
              background: bank.verified ? (darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4') : (darkMode ? 'rgba(234,179,8,0.12)' : '#fefce8'),
              border: `1px solid ${bank.verified ? '#86efac' : '#fde047'}` }}>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: bank.verified ? '#16a34a' : '#a16207', margin: 0 }}>{bank.verified ? '✓ Verified for payouts' : 'Not verified — payouts blocked'}</p>
                <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>
                  Last changed {bank.updated_at ? new Date(bank.updated_at).toLocaleDateString('en-IN') : '—'}{bank.updated_by === 'partner' ? ' by the partner' : ''}
                </p>
              </div>
              {isAdminUser() && (
                <button disabled={saving} onClick={() => call('bank/verify', { verified: !bank.verified }, 'Updated')}
                  style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: bank.verified ? T.bg2 : 'linear-gradient(135deg,#16a34a,#15803d)', color: bank.verified ? T.text2 : '#fff' }}>
                  {bank.verified ? 'Remove verification' : 'Mark verified'}
                </button>
              )}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: T.text3, margin: 0 }}>No bank account on file yet. The partner can add it from their portal / app, or you can add it here.</p>
          )}
          <div>
            <label style={lbl}>Account holder name *</label>
            <input value={form.bank_account_name} onChange={e => setForm(f => ({ ...f, bank_account_name: e.target.value }))} style={is} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Bank name *</label>
              <input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g. SBI" style={is} />
            </div>
            <div>
              <label style={lbl}>IFSC *</label>
              <input value={form.bank_ifsc} onChange={e => setForm(f => ({ ...f, bank_ifsc: e.target.value.toUpperCase() }))} placeholder="SBIN0001234" style={is} />
            </div>
          </div>
          <div>
            <label style={lbl}>Account number *</label>
            <input value={form.bank_account_number} onFocus={() => masked && setForm(f => ({ ...f, bank_account_number: '' }))}
              onChange={e => setForm(f => ({ ...f, bank_account_number: e.target.value.replace(/\D/g, '') }))} style={is} />
          </div>
          <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>Saving a change removes the verification — an admin must verify the new account before any payout.</p>
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Close</button>
          <button disabled={saving || masked} onClick={() => call('bank', form, 'Saved')}
            style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (saving || masked) ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save bank details'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pays to the partner's VERIFIED bank account on file — nothing to type each time.
function EncashModal({ partner, onClose, onSuccess, darkMode, T }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({ amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [showBank, setShowBank] = useState(false);
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/bank`, { headers: { Authorization: `Bearer ${token}` } });
      setInfo(await res.json());
    } catch (_) {}
  }, [partner.id, token]);
  useEffect(() => { load(); }, [load]);

  const quarters = () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const labels = ['Q4 (Jan-Mar)', 'Q1 (Apr-Jun)', 'Q2 (Jul-Sep)', 'Q3 (Oct-Dec)'];
    const ends = ['31 Mar', '30 Jun', '30 Sep', '31 Dec'];
    return { label: labels[q], end: ends[q], year: now.getFullYear() };
  };
  const qtr = quarters();
  const bank = info?.bank;
  const available = Number(info?.summary?.available || 0);
  const onHold = Number(info?.summary?.on_hold || 0);
  const canPay = bank?.verified && available > 0;

  const handleEncash = async () => {
    if (!form.amount) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/encash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, amount: Number(form.amount), quarter: qtr.label })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { showToast(d.message || 'Encash recorded', 'success'); onSuccess(); onClose(); }
      else showToast(d.message || 'Could not encash', 'error');
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#f97316,#ea580c)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Encash Credits</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>{qtr.label} · Cycle ends {qtr.end} {qtr.year}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: darkMode ? 'rgba(249,115,22,0.1)' : '#fff7ed', border: `1px solid ${darkMode ? 'rgba(249,115,22,0.25)' : '#fed7aa'}`, borderRadius: 10, padding: '10px 14px' }}>
            <p style={{ fontSize: 12, color: '#f97316', fontWeight: 600, margin: 0 }}>Available to pay out: {fmt(available)}</p>
            {onHold > 0 && <p style={{ fontSize: 11, color: T.text2, margin: '3px 0 0' }}>{fmt(onHold)} more is credited but on hold — its invoice isn't fully paid or the work isn't completed yet.</p>}
            <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>Already net of any TDS deducted when each commission was approved</p>
          </div>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', background: T.bg2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', margin: 0 }}>Pay to</p>
              {bank ? (
                <>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '3px 0 0' }}>{bank.account_name} · {bank.bank_name}</p>
                  <p style={{ fontSize: 12, color: T.text2, margin: '2px 0 0' }}>A/c {bank.account_number} · {bank.ifsc}</p>
                  <p style={{ fontSize: 11, fontWeight: 600, color: bank.verified ? '#16a34a' : '#a16207', margin: '3px 0 0' }}>{bank.verified ? '✓ Verified' : 'Not verified — verify before paying'}</p>
                </>
              ) : <p style={{ fontSize: 12, color: '#a16207', margin: '3px 0 0' }}>No bank account on file</p>}
            </div>
            <button onClick={() => setShowBank(true)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg3, color: T.text2, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
              {bank ? 'Manage' : 'Add'}
            </button>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Amount to Encash *</label>
            <input type="number" min={1} max={available} value={form.amount} disabled={!canPay} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder={`Max ${fmt(available)}`} style={is} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Notes / transfer reference</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. NEFT UTR number" style={is} />
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleEncash} disabled={saving || !form.amount || !canPay} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!form.amount || saving || !canPay) ? 0.6 : 1 }}>
            {saving ? 'Processing...' : 'Record Payout'}
          </button>
        </div>
      </div>
      {showBank && <BankModal partner={partner} onClose={() => { setShowBank(false); load(); }} darkMode={darkMode} T={T} />}
    </div>
  );
}

// ─── Payout requests raised by partners — an Admin approves or rejects ────────
function PayoutRequestsTab({ requests, onRefresh, darkMode, T, token }) {
  const { showToast, showConfirm } = useToast();
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('pending');
  const admin = isAdminUser();
  const rows = requests.filter(r => filter === 'all' || r.status === filter);

  const decide = async (r, action, note) => {
    setBusy(r.id);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/payout-requests/${r.id}/${action}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ note }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { showToast(d.message || 'Done', 'success'); setRejecting(null); setReason(''); onRefresh(); }
      else showToast(d.message || 'Could not update', 'error');
    } catch (_) { showToast('Server error', 'error'); }
    setBusy(null);
  };
  const approve = async (r) => {
    const acct = r.bank ? `${r.bank.bank_name} A/c ${r.bank.account_number} (${r.bank.ifsc})` : 'no account';
    if (!(await showConfirm(`Pay ${fmt(r.amount)} to ${r.partner_name} — ${acct}? Make the bank transfer, then confirm. This records the payout and locks the commissions it uses.`,
      { title: 'Approve payout', confirmLabel: 'Approve & record', confirmColor: '#16a34a' }))) return;
    decide(r, 'approve', null);
  };

  const STATUS = {
    pending: { label: 'Pending', color: '#a16207', bg: darkMode ? 'rgba(234,179,8,0.12)' : '#fefce8' },
    approved: { label: 'Paid', color: '#16a34a', bg: darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4' },
    rejected: { label: 'Rejected', color: '#dc2626', bg: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2' },
    cancelled: { label: 'Cancelled by partner', color: T.text3, bg: T.bg3 },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {['pending', 'approved', 'rejected', 'cancelled', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${filter === f ? '#1a3a6b' : T.border2}`, background: filter === f ? '#1a3a6b' : T.bg2, color: filter === f ? '#fff' : T.text2 }}>
            {f === 'all' ? 'All' : STATUS[f].label}
          </button>
        ))}
      </div>
      {!admin && <p style={{ fontSize: 12, color: T.text3, margin: 0 }}>Only an Admin can approve or reject payout requests.</p>}
      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: T.text3, fontSize: 13 }}>No {filter === 'all' ? '' : STATUS[filter].label.toLowerCase()} payout requests</div>
      ) : rows.map(r => {
        const st = STATUS[r.status] || STATUS.pending;
        const shortBy = r.status === 'pending' && r.summary && Number(r.amount) > Number(r.summary.available) + 0.004;
        return (
          <div key={r.id} style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '14px 18px', display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{r.partner_name}</span>
                <span style={{ fontSize: 11, color: T.text3 }}>{r.referral_partner_id} · Request #{r.id}</span>
                <Badge label={st.label} color={st.color} bg={st.bg} />
              </div>
              <p style={{ fontSize: 12, color: T.text2, margin: '6px 0 0' }}>
                Requested {new Date(r.requested_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                {r.partner_note ? ` · “${r.partner_note}”` : ''}
              </p>
              {r.bank && (
                <p style={{ fontSize: 12, color: T.text2, margin: '4px 0 0' }}>
                  {r.bank.account_name} · {r.bank.bank_name} · A/c {r.bank.account_number} · {r.bank.ifsc}{' '}
                  <span style={{ fontWeight: 600, color: r.bank.verified ? '#16a34a' : '#a16207' }}>{r.bank.verified ? '✓ Verified' : 'Not verified'}</span>
                </p>
              )}
              {r.status === 'pending' && r.summary && (
                <p style={{ fontSize: 11, color: shortBy ? '#dc2626' : T.text3, margin: '4px 0 0' }}>
                  Available now {fmt(r.summary.available)}{Number(r.summary.on_hold) > 0 ? ` · on hold ${fmt(r.summary.on_hold)}` : ''}
                  {shortBy ? ' — not enough available (an invoice may have been cancelled or work reopened).' : ''}
                </p>
              )}
              {r.decision_note && <p style={{ fontSize: 12, color: T.text2, margin: '4px 0 0' }}>Note: {r.decision_note}</p>}
              {r.decided_at && r.status !== 'cancelled' && <p style={{ fontSize: 11, color: T.text3, margin: '4px 0 0' }}>{st.label} by {r.decided_by_name || '—'} on {new Date(r.decided_at).toLocaleDateString('en-IN')}</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{fmt(r.amount)}</span>
              {admin && r.status === 'pending' && (
                rejecting === r.id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (partner will see it)"
                      style={{ border: `1px solid ${T.inputBorder}`, borderRadius: 8, padding: '6px 10px', fontSize: 12, background: T.inputBg, color: T.text, width: 220 }} />
                    <button disabled={!reason.trim() || busy === r.id} onClick={() => decide(r, 'reject', reason)}
                      style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: reason.trim() ? 1 : 0.6 }}>Reject</button>
                    <button onClick={() => { setRejecting(null); setReason(''); }} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button disabled={busy === r.id} onClick={() => setRejecting(r.id)}
                      style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid #fca5a5`, background: T.bg2, color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Reject</button>
                    <button disabled={busy === r.id || !r.bank?.verified || shortBy} onClick={() => approve(r)}
                      title={!r.bank?.verified ? 'Verify the bank account first' : shortBy ? 'Not enough available' : ''}
                      style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!r.bank?.verified || shortBy) ? 0.5 : 1 }}>
                      {busy === r.id ? 'Saving...' : 'Approve & pay'}
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── View Partner Details — identity, KYC, bank, balances ─────────────────────
// Opens from the partner's name. PAN + email come from the client (they identify
// the person: one person, one Referral ID); the KYC form captures the rest.
// Editing KYC resets it to pending; only an Admin verifies it.
const KYC_BLANK = { kyc_legal_name: '', kyc_father_name: '', kyc_dob: '', kyc_occupation: '', kyc_alt_phone: '',
  kyc_address: '', kyc_city: '', kyc_state: '', kyc_pincode: '', kyc_aadhaar_last4: '', gstn: '', kyc_notes: '' };

function PartnerDetailsModal({ partner, onClose, onChanged, onEdit, onLeads, darkMode, T }) {
  const { showToast } = useToast();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [editKyc, setEditKyc] = useState(false);
  const [form, setForm] = useState(KYC_BLANK);
  const [saving, setSaving] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const admin = isAdminUser();

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/kyc`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) { showToast(d.message || 'Could not load partner', 'error'); return; }
      setData(d);
      setForm({ ...KYC_BLANK, ...Object.fromEntries(Object.keys(KYC_BLANK).map(k => [k, d.kyc?.[k] || ''])) });
    } catch (_) { showToast('Server error', 'error'); }
  }, [partner.id, token, showToast]);
  useEffect(() => { load(); }, [load]);

  const send = async (path, body, after) => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/${path}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { showToast(d.message || 'Saved', 'success'); after && after(); await load(); onChanged && onChanged(); }
      else showToast(d.message || 'Could not save', 'error');
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const p = data?.partner || partner;
  const kyc = data?.kyc || {};
  const bank = data?.bank;
  const sum = data?.summary || {};
  const is = { width: '100%', border: `1px solid ${T.inputBorder || T.border2}`, borderRadius: 9, padding: '8px 11px', fontSize: 13, outline: 'none', background: T.inputBg || T.bg2, color: T.text, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 10, fontWeight: 700, color: T.text3, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const card = { background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px' };
  const h4 = { fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const Row = ({ k, v, warn }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 13 }}>
      <span style={{ color: T.text3 }}>{k}</span>
      <span style={{ color: warn ? '#dc2626' : T.text, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v || (warn ? 'Missing' : '—')}</span>
    </div>
  );
  // A plain function (not a component): a component defined here would remount
  // its input on every keystroke and lose focus.
  const F = ({ k, label, span, ...rest }) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <label style={lbl}>{label}</label>
      <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={is} {...rest} />
    </div>
  );
  const kycVerified = kyc.status === 'verified';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg3, borderRadius: 20, width: 'min(96vw, 980px)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 17, fontWeight: 700, flexShrink: 0 }}>{(p.name || 'P').charAt(0).toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>{p.name}</h3>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', margin: '3px 0 0' }}>
                {p.referral_partner_id} · {p.status === 'active' ? 'Active' : 'Inactive'}{p.client_name ? ` · Client: ${p.client_name}${p.file_number ? ` (${p.file_number})` : ''}` : ' · External partner'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={onLeads} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Referrals</button>
            {admin && <button onClick={onEdit} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>}
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <div className="rp-details-grid rp-noscroll" style={{ padding: 20, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16, alignItems: 'start' }}>
          {!data ? <p style={{ color: T.text3, fontSize: 13 }}>Loading…</p> : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={card}>
                  <h4 style={h4}>Identity — one person, one Referral ID</h4>
                  <Row k="PAN" v={p.pan} warn={!p.pan} />
                  <Row k="Email (portal login)" v={p.email} warn={!p.email} />
                  <Row k="Phone" v={p.phone} />
                  <Row k="Partner since" v={p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : null} />
                  {!p.pan && <p style={{ fontSize: 11, color: '#dc2626', margin: '6px 0 0' }}>Add the PAN in the client profile — it is needed for TDS and to keep one Referral ID per person.</p>}
                </div>
                <div style={card}>
                  <h4 style={h4}>Commission & balance</h4>
                  <Row k="Rate" v={partner.commission_type === 'percentage' ? `${Number(partner.commission_value || 0)}% per referral` : `${fmt(partner.commission_value)} per referral`} />
                  <Row k="Total earned (net)" v={fmt(partner.total_earned)} />
                  <Row k="Withdrawable now" v={fmt(sum.available)} />
                  <Row k="On hold (invoice / work pending)" v={fmt(sum.on_hold)} />
                  <Row k="Paid out / redeemed" v={`${fmt(partner.total_encashed)} / ${fmt(partner.total_redeemed)}`} />
                </div>
                <div style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ ...h4, margin: 0 }}>Bank account</h4>
                    <button onClick={() => setShowBank(true)} style={{ padding: '5px 11px', borderRadius: 7, border: `1px solid ${T.border2}`, background: T.bg3, color: T.text2, fontSize: 12, cursor: 'pointer' }}>{bank ? 'Manage' : 'Add'}</button>
                  </div>
                  {bank ? (
                    <div style={{ marginTop: 8 }}>
                      <Row k="Holder" v={bank.account_name} />
                      <Row k="Bank" v={bank.bank_name} />
                      <Row k="Account" v={bank.account_number} />
                      <Row k="IFSC" v={bank.ifsc} />
                      <p style={{ fontSize: 12, fontWeight: 700, margin: '6px 0 0', color: bank.verified ? '#16a34a' : '#a16207' }}>
                        {bank.verified ? '✓ Verified for payouts' : 'Not verified — payouts blocked'}{bank.updated_by === 'partner' ? ' · added by the partner' : ''}
                      </p>
                    </div>
                  ) : <p style={{ fontSize: 12, color: T.text3, margin: '8px 0 0' }}>No bank account yet. The partner can add it from their portal / app, or add it here.</p>}
                </div>
              </div>

              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h4 style={{ ...h4, margin: 0 }}>KYC details</h4>
                    <Badge label={kycVerified ? '✓ KYC verified' : 'KYC pending'} color={kycVerified ? '#16a34a' : '#a16207'} bg={kycVerified ? (darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4') : (darkMode ? 'rgba(234,179,8,0.12)' : '#fefce8')} />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {admin && !editKyc && kyc.kyc_legal_name && (
                      <button disabled={saving} onClick={() => send('kyc/verify', { verified: !kycVerified })}
                        style={{ padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: kycVerified ? T.bg3 : 'linear-gradient(135deg,#16a34a,#15803d)', color: kycVerified ? T.text2 : '#fff' }}>
                        {kycVerified ? 'Remove verification' : 'Mark KYC verified'}
                      </button>
                    )}
                    {admin && !editKyc && (
                      <button onClick={() => setEditKyc(true)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg3, color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {kyc.kyc_legal_name ? 'Edit KYC' : 'Fill KYC'}
                      </button>
                    )}
                  </div>
                </div>
                {kycVerified && kyc.verified_at && <p style={{ fontSize: 11, color: T.text3, margin: '-6px 0 10px' }}>Verified by {kyc.verified_by_name || '—'} on {new Date(kyc.verified_at).toLocaleDateString('en-IN')}</p>}

                {editKyc ? (
                  <>
                    <div className="rp-kyc-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {F({ k: "kyc_legal_name", label: "Name as per PAN *", })}
                      {F({ k: 'kyc_father_name', label: "Father's / spouse's name" })}
                      {F({ k: "kyc_dob", label: "Date of birth", type: "date", })}
                      {F({ k: 'kyc_occupation', label: 'Occupation / profession', placeholder: 'e.g. Business, Service, Professional' })}
                      {F({ k: "kyc_alt_phone", label: "Alternate phone", inputMode: "tel", })}
                      {F({ k: "kyc_aadhaar_last4", label: "Aadhaar — last 4 digits only", maxLength: 4, inputMode: "numeric", })}
                      {F({ k: "kyc_address", label: "Address", span: true, })}
                      {F({ k: "kyc_city", label: "City", })}
                      {F({ k: "kyc_state", label: "State", })}
                      {F({ k: "kyc_pincode", label: "PIN code", maxLength: 6, inputMode: "numeric", })}
                      {F({ k: "gstn", label: "GSTIN (if registered)", })}
                      {F({ k: "kyc_notes", label: "Notes", span: true, })}
                    </div>
                    <p style={{ fontSize: 11, color: T.text3, margin: '10px 0 0' }}>PAN and email come from the client record. Saving changes resets KYC to pending until an Admin verifies it.</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <button onClick={() => { setEditKyc(false); load(); }} style={{ padding: '8px 16px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                      <button disabled={saving} onClick={() => send('kyc', form, () => setEditKyc(false))}
                        style={{ padding: '8px 20px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Saving…' : 'Save KYC'}
                      </button>
                    </div>
                  </>
                ) : kyc.kyc_legal_name ? (
                  <div className="rp-kyc-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 20 }}>
                    <Row k="Name as per PAN" v={kyc.kyc_legal_name} />
                    <Row k="Father / spouse" v={kyc.kyc_father_name} />
                    <Row k="Date of birth" v={kyc.kyc_dob ? new Date(kyc.kyc_dob).toLocaleDateString('en-IN') : null} />
                    <Row k="Occupation" v={kyc.kyc_occupation} />
                    <Row k="Alternate phone" v={kyc.kyc_alt_phone} />
                    <Row k="Aadhaar" v={kyc.kyc_aadhaar_last4 ? `XXXX XXXX ${kyc.kyc_aadhaar_last4}` : null} />
                    <div style={{ gridColumn: '1 / -1' }}><Row k="Address" v={[kyc.kyc_address, kyc.kyc_city, kyc.kyc_state, kyc.kyc_pincode].filter(Boolean).join(', ')} /></div>
                    <Row k="GSTIN" v={kyc.gstn} />
                    <Row k="Notes" v={kyc.kyc_notes} />
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: T.text3, margin: 0 }}>KYC not filled yet.{admin ? ' Click "Fill KYC" to capture the partner\'s details.' : ''}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showBank && <BankModal partner={partner} onClose={() => { setShowBank(false); load(); }} onSuccess={onChanged} darkMode={darkMode} T={T} />}
      <style>{`.rp-noscroll::-webkit-scrollbar { display: none; } @media (max-width: 760px) { .rp-details-grid, .rp-kyc-form { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

// ─── Performance Tab ──────────────────────────────────────────────────────────
function PerformanceTab({ partners, darkMode, T }) {
  const sorted = [...partners].sort((a, b) => (b.total_earned || 0) - (a.total_earned || 0));
  const maxRevenue = Math.max(...partners.map(p => p.revenue_generated || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        {[
          { label: 'Total Partners', value: fmtN(partners.length), color: '#3b82f6' },
          { label: 'Active Partners', value: fmtN(partners.filter(p => p.status === 'active').length), color: '#16a34a' },
          { label: 'Total Commissions', value: fmt(partners.reduce((s, p) => s + (Number(p.total_earned) || 0), 0)), color: '#f97316' },
          { label: 'Revenue via Referral', value: fmt(partners.reduce((s, p) => s + (p.revenue_generated || 0), 0)), color: '#8b5cf6' },
        ].map((stat, i) => (
          <div key={i} style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '16px 20px', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
            <p style={{ fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', margin: '0 0 8px' }}>{stat.label}</p>
            <p style={{ fontSize: 20, fontWeight: 800, color: stat.color, margin: 0 }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden', boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Partner Leaderboard</p>
          <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>Ranked by commission earned</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
            <tr>
              {['#', 'Partner', 'Type', 'Leads', 'Converted', 'Revenue', 'Commission', 'Balance'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '48px 16px', textAlign: 'center', color: T.text3, fontSize: 13 }}>No partners yet</td></tr>
            ) : sorted.map((p, i) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}`, background: darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff'), transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
                onMouseLeave={e => e.currentTarget.style.background = darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff')}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: i < 3 ? ['#f59e0b', '#9ca3af', '#b45309'][i] : T.bg3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: i < 3 ? '#fff' : T.text3 }}>
                    {i + 1}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0 }}>{p.name}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>{p.referral_partner_id}</p>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Badge label={p.partner_type === 'client' ? 'Client' : 'External'} color={p.partner_type === 'client' ? '#3b82f6' : '#8b5cf6'} bg={p.partner_type === 'client' ? (darkMode ? 'rgba(59,130,246,0.12)' : '#eff6ff') : (darkMode ? 'rgba(139,92,246,0.12)' : '#f5f3ff')} />
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2, fontWeight: 600 }}>{fmtN(p.total_leads || 0)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{fmtN(p.converted_leads || 0)}</span>
                  <span style={{ fontSize: 11, color: T.text3 }}> ({(p.total_leads && p.converted_leads) ? Math.round((Number(p.converted_leads) / Number(p.total_leads)) * 100) : 0}%)</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 60, height: 6, borderRadius: 3, background: T.bg3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#8b5cf6', width: `${((p.revenue_generated || 0) / maxRevenue) * 100}%`, borderRadius: 3, transition: 'width 0.4s' }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#8b5cf6' }}>{fmt(p.revenue_generated || 0)}</span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#f97316' }}>{fmt(p.total_earned || 0)}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: (p.credit_balance || 0) > 0 ? '#16a34a' : T.text3 }}>{fmt(p.credit_balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN MODULE ──────────────────────────────────────────────────────────────
// ─── Commissions Tab ──────────────────────────────────────────────────────────
function CommissionsTab({ commissions, partners, onRefresh, onManualAdd, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast, showConfirm } = useToast();
  const [approving, setApproving] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [approveTds, setApproveTds] = useState(false);
  const [approveSection, setApproveSection] = useState('194H');
  const [approveRate, setApproveRate] = useState(2);
  const [editModal, setEditModal] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  const openApprove = (commission) => {
    setApproveModal(commission);
    setApproveTds(Number(commission.tds_amount) > 0);
    setApproveSection(commission.tds_section || '194H');
    setApproveRate(Number(commission.tds_rate) || 2);
  };

  const handleApprove = async () => {
    const commission = approveModal;
    setApproving(commission.id);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/${commission.id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deduct_tds: approveTds, tds_section: approveSection, tds_rate: approveRate }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { showToast(d.message || 'Commission approved and credited', 'success'); setApproveModal(null); onRefresh(); }
      else { showToast(d.message || 'Could not approve commission', 'error'); }
    } catch (_) { showToast('Server error while approving commission', 'error'); }
    setApproving(null);
  };

  const handleReject = async (commission) => {
    if (!(await showConfirm('Reject this commission?', { confirmLabel: 'Reject', confirmColor: '#ef4444' }))) return;
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/${commission.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(d.message || 'Could not reject commission', 'error'); return; }
      showToast('Commission rejected', 'success');
      onRefresh();
    } catch (_) { showToast('Server error while rejecting commission', 'error'); }
  };

  const handleEdit = async () => {
    if (!editAmount) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/commissions/${editModal.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ commission_amount: editAmount, notes: editNote }),
      });
      showToast('Commission override saved', 'success');
      setEditModal(null);
      onRefresh();
    } catch (_) { showToast('Server error while saving override', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!(await showConfirm('Delete this commission entry? This cannot be undone.', { confirmLabel: 'Delete', confirmColor: '#ef4444' }))) return;
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(d.message || 'Could not delete commission', 'error'); return; }
      showToast('Commission entry deleted', 'success');
      onRefresh();
    } catch (_) { showToast('Server error while deleting commission', 'error'); }
  };

  const statusColor = (s) => ({
    pending:  { color:'#f59e0b', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb',  border: darkMode?'rgba(245,158,11,0.3)':'#fde68a'  },
    approved: { color:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4',  border: darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'   },
    rejected: { color:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2',  border: darkMode?'rgba(220,38,38,0.3)':'#fecaca'   },
    manual:   { color:'#8b5cf6', bg: darkMode?'rgba(139,92,246,0.12)':'#f5f3ff', border: darkMode?'rgba(139,92,246,0.3)':'#ddd6fe'  },
  }[s] || { color:'#94a3b8', bg:T.bg3, border:T.border });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Approve & Credit Modal */}
      {approveModal && (() => {
        const amount = Number(approveModal.commission_amount || 0);
        const partner = partners.find(p => p.id === approveModal.partner_id);
        const fyCredited = Number(partner?.fy_commission_credited || 0);
        const wouldCrossThreshold = (fyCredited + amount) > 15000;
        const tdsPreview = approveTds ? Math.round(amount * Number(approveRate || 0)) / 100 : 0;
        const netPreview = Math.max(0, amount - tdsPreview);
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
            <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:440, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
              <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#16a34a,#15803d)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Approve &amp; Credit</h3>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>{approveModal.partner_name} · {fmt(amount)}</p>
                </div>
                <button onClick={()=>setApproveModal(null)} style={{ width:28, height:28, borderRadius:7, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:12 }}>
                <p style={{ fontSize:11, color:T.text3, margin:0 }}>
                  FY commission credited so far: {fmt(fyCredited)} · Threshold ₹15,000 (Sec 194H)
                  {partner && !partner.pan && <span style={{ color:'#dc2626' }}> · No PAN on file</span>}
                </p>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" checked={approveTds} onChange={e=>setApproveTds(e.target.checked)} />
                  <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Deduct TDS before crediting</span>
                </label>
                {!wouldCrossThreshold && (
                  <p style={{ fontSize:11, color:'#16a34a', margin:0 }}>Aggregate stays within ₹15,000 for the FY — TDS not mandatory.</p>
                )}
                {approveTds && (
                  <>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <select value={approveSection} onChange={e=>setApproveSection(e.target.value)} style={is}>
                        <option value="194H">194H — Commission/Brokerage</option>
                        <option value="194J">194J — Professional Fees</option>
                        <option value="194C">194C — Contractor Payment</option>
                      </select>
                      <input type="number" min={0} step="0.01" value={approveRate} onChange={e=>setApproveRate(e.target.value)} placeholder="Rate %" style={is} />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                      <span style={{ color:T.text2 }}>TDS: <strong style={{ color:'#dc2626' }}>−{fmt(tdsPreview)}</strong></span>
                      <span style={{ color:T.text2 }}>Net to credit: <strong style={{ color:'#16a34a' }}>{fmt(netPreview)}</strong></span>
                    </div>
                  </>
                )}
                {!approveTds && (
                  <div style={{ fontSize:13, color:T.text2 }}>Full amount to credit: <strong style={{ color:'#16a34a' }}>{fmt(amount)}</strong></div>
                )}
              </div>
              <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
                <button onClick={()=>setApproveModal(null)} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
                <button onClick={handleApprove} disabled={approving===approveModal.id}
                  style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:approving===approveModal.id?0.6:1 }}>
                  {approving===approveModal.id ? 'Approving...' : 'Approve & Credit'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit Modal */}
      {editModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:420, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Edit Commission</h3>
              <button onClick={()=>setEditModal(null)} style={{ width:28, height:28, borderRadius:7, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase' }}>Commission Amount (₹) *</label>
                <input type="number" value={editAmount} onChange={e=>setEditAmount(e.target.value)} style={is} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase' }}>Override Note</label>
                <input type="text" value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="Reason for edit (admin override)" style={is} />
              </div>
              <div style={{ background:darkMode?'rgba(249,115,22,0.1)':'#fff7ed', border:`1px solid ${darkMode?'rgba(249,115,22,0.25)':'#fed7aa'}`, borderRadius:9, padding:'10px 14px' }}>
                <p style={{ fontSize:12, color:'#f97316', margin:0, fontWeight:600 }}>⚠ Admin Override — credit balance will be adjusted automatically</p>
              </div>
            </div>
            <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setEditModal(null)} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleEdit} disabled={saving||!editAmount} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!editAmount)?0.6:1 }}>
                {saving?'Saving...':'Save Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Commission Approvals</p>
          <p style={{ fontSize:12, color:T.text3, margin:'3px 0 0' }}>{commissions.filter(c=>c.status==='pending' && !c.approval_blocker).length} ready to approve · {commissions.filter(c=>c.status==='pending' && c.approval_blocker).length} awaiting payment · {commissions.length} total</p>
        </div>
        {isAdminUser() && (
        <button onClick={() => onManualAdd(partners[0] || {})}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Manual Credit
        </button>
        )}
      </div>

      {/* Commission list */}
      {commissions.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}` }}>
          <div style={{ width:52, height:52, borderRadius:14, background:T.bg3, border:`2px dashed ${T.border2}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          </div>
          <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:0 }}>No commissions yet</p>
          <p style={{ fontSize:12, color:T.text3, marginTop:6 }}>Commissions appear here when referred tasks are fully paid</p>
        </div>
      ) : (
        <div style={{ background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow:darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Partner','Type','Referred / Reason','Invoice Amt','Commission','TDS','Status','Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commissions.map((c, i) => {
                const sc = statusColor(c.status);
                const isPending = c.status === 'pending';
                return (
                  <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}`, background:darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'12px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{c.partner_name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{c.referral_partner_id}</p>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <Badge label={c.commission_source === 'manual' ? 'Manual' : 'Auto'} color={c.commission_source==='manual'?'#8b5cf6':'#3b82f6'} bg={c.commission_source==='manual'?(darkMode?'rgba(139,92,246,0.12)':'#f5f3ff'):(darkMode?'rgba(59,130,246,0.12)':'#eff6ff')} />
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2, maxWidth:160 }}>
                      <p style={{ margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.referred_name || c.task_title || c.service_name || c.reason || '—'}</p>
                      {c.notes && <p style={{ margin:'2px 0 0', fontSize:11, color:T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.notes}</p>}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:T.text2 }}>{c.invoice_amount ? fmt(c.invoice_amount) : '—'}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <p style={{ fontSize:14, fontWeight:700, color:'#f97316', margin:0 }}>{fmt(c.commission_amount)}</p>
                      {Number(c.tds_amount) > 0 && c.status === 'approved' && (
                        <p style={{ fontSize:11, color:'#16a34a', margin:'2px 0 0' }}>Net: {fmt(c.net_amount)}</p>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2 }}>
                      {Number(c.tds_amount) > 0 ? (
                        <><span style={{ color:'#dc2626', fontWeight:600 }}>−{fmt(c.tds_amount)}</span><br/><span style={{ fontSize:10, color:T.text3 }}>{c.tds_section} @ {c.tds_rate}%</span></>
                      ) : '—'}
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, whiteSpace:'nowrap' }}>{c.display || c.status}</span>
                      {c.invoice_number && <p style={{ fontSize:10, color:T.text3, margin:'4px 0 0' }}>{c.invoice_number}{c.invoice_status ? ` · ${c.invoice_status.replace(/_/g,' ')}` : ''}</p>}
                      {c.hold_reason && <p style={{ fontSize:10, color:'#a16207', fontWeight:600, margin:'4px 0 0' }}>⏸ Not withdrawable yet · {c.hold_reason}</p>}
                      {c.revised_amount != null && (
                        <p title="The invoice changed after this amount was fixed. The commission was not changed automatically." style={{ fontSize:10, color:'#d97706', fontWeight:600, margin:'4px 0 0' }}>
                          ⚠ Invoice revised · now {fmt(c.revised_amount)}
                        </p>
                      )}
                      {Number(c.paid_out_amount) > 0 && (
                        <p style={{ fontSize:10, color:'#16a34a', fontWeight:600, margin:'4px 0 0' }}>🔒 {fmt(c.paid_out_amount)} paid out · locked</p>
                      )}
                      {c.payout_conflict && (
                        <p title={c.payout_conflict} style={{ fontSize:10, color:'#b45309', fontWeight:600, margin:'4px 0 0', maxWidth:220 }}>⚠ {c.payout_conflict}</p>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        {!isAdminUser() && <span style={{ fontSize:11, color:T.text3 }}>Admin only</span>}
                        {isAdminUser() && isPending && (
                          <>
                            <button onClick={() => !c.approval_blocker && openApprove(c)} disabled={approving===c.id || !!c.approval_blocker}
                              title={c.approval_blocker ? `${c.approval_blocker} — no payment, no credit` : 'Approve & credit'}
                              style={{ padding:'5px 12px', borderRadius:7, border:'none', background: c.approval_blocker ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:12, fontWeight:600, cursor: c.approval_blocker ? 'not-allowed' : 'pointer', opacity:approving===c.id?0.6:1 }}>
                              {approving===c.id ? '...' : 'Approve'}
                            </button>
                            <button onClick={() => handleReject(c)}
                              style={{ padding:'5px 10px', borderRadius:7, border:`1px solid ${darkMode?'rgba(220,38,38,0.3)':'#fecaca'}`, background:'none', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              Reject
                            </button>
                          </>
                        )}
                        {/* Admin Override — Edit & Delete */}
                        {isAdminUser() && <>
                        <button onClick={() => { setEditModal(c); setEditAmount(c.commission_amount); setEditNote(''); }}
                          title="Edit (Admin Override)"
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#f97316';e.currentTarget.style.borderColor='#f97316';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button onClick={() => Number(c.paid_out_amount) > 0 ? showToast('This commission has been paid out (encash/redeem), so it can never be deleted.', 'error') : handleDelete(c.id)}
                          title={Number(c.paid_out_amount) > 0 ? 'Paid out — can never be deleted' : 'Delete (Admin Override)'}
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor: Number(c.paid_out_amount) > 0 ? 'not-allowed' : 'pointer', opacity: Number(c.paid_out_amount) > 0 ? 0.35 : 1 }}
                          onMouseEnter={e=>{e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2';e.currentTarget.style.borderColor='#ef4444';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>
                        </>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Manual Commission Modal ───────────────────────────────────────────────────
function ManualCommissionModal({ partner: initialPartner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const [partners, setPartners] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({
    partner_id: initialPartner?.id || '',
    referral_lead_id: '',
    commission_amount: '',
    reason: '',
    notes: '',
    invoice_ref: '',
    deduct_tds: false,
    tds_section: '194H',
    tds_rate: 2,
  });
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  useEffect(() => {
    fetch(`${BASE}/api/referral-partners?status=active`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r=>r.json()).then(d=>setPartners(Array.isArray(d)?d:[])).catch(()=>{});
  }, [token]);

  useEffect(() => {
    if (!form.partner_id) { setLeads([]); return; }
    fetch(`${BASE}/api/referral-partners/${form.partner_id}/leads`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r=>r.json()).then(d=>setLeads(Array.isArray(d)?d:[])).catch(()=>setLeads([]));
  }, [form.partner_id, token]);

  const selectedPartner = partners.find(p => String(p.id) === String(form.partner_id));
  const fyCredited = Number(selectedPartner?.fy_commission_credited || 0);
  const amount = Number(form.commission_amount || 0);
  const wouldCrossThreshold = (fyCredited + amount) > 15000;
  // Deducting is the admin's explicit choice (the checkbox) — the threshold
  // below is only advisory (mandatory vs optional), it never zeroes this out.
  const tdsPreview = form.deduct_tds ? Math.round(amount * Number(form.tds_rate || 0)) / 100 : 0;
  const netPreview = Math.max(0, amount - tdsPreview);

  const handleSave = async () => {
    if (!form.partner_id || !form.commission_amount || !form.reason) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/manual`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ ...form, referral_lead_id: form.referral_lead_id || null }),
      });
      if (res.ok) { showToast('Manual credit submitted for approval', 'success'); onSuccess(); }
      else { const d = await res.json().catch(()=>({})); showToast(d.message || 'Could not submit manual credit', 'error'); }
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const label = { display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'min(94vw, 760px)', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Add Manual Credit</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>Requires admin approval before crediting</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div style={{ padding:'20px 24px', background:T.bg3, overflowY:'auto', scrollbarWidth:'none', display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
          {/* LEFT — what & who */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={label}>Partner *</label>
              <select value={form.partner_id} onChange={e=>setForm(f=>({...f,partner_id:e.target.value}))} style={is}>
                <option value="">Select partner...</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.referral_partner_id})</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Referred Task / Lead</label>
              <select value={form.referral_lead_id} onChange={e=>{
                const rl = leads.find(l => String(l.id) === e.target.value);
                setForm(f=>({ ...f, referral_lead_id: e.target.value,
                  reason: rl && !f.reason ? `Referral for ${rl.lead_name || rl.contact_person || 'lead'}` : f.reason }));
              }} disabled={!form.partner_id} style={is}>
                <option value="">General (not tied to a specific referral)</option>
                {leads.map(l => (
                  <option key={l.id} value={l.id}>
                    {(l.lead_name || l.contact_person || 'Unnamed')} — {(l.status || 'lead').replace(/_/g,' ')}
                  </option>
                ))}
              </select>
              {form.partner_id && leads.length === 0 && (
                <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>This partner has no referred leads yet.</p>
              )}
            </div>
            <div>
              <label style={label}>Commission Amount (₹) *</label>
              <input type="number" min={1} value={form.commission_amount} onChange={e=>setForm(f=>({...f,commission_amount:e.target.value}))} placeholder="e.g. 1000" style={is} />
            </div>
            <div>
              <label style={label}>Reason *</label>
              <input type="text" value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Referral for Annual Filing — Pinaak Sarang" style={is} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={label}>Invoice Reference</label>
                <input type="text" value={form.invoice_ref} onChange={e=>setForm(f=>({...f,invoice_ref:e.target.value}))} placeholder="INV-26-27-001" style={is} />
              </div>
              <div>
                <label style={label}>Notes</label>
                <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Optional" style={is} />
              </div>
            </div>
          </div>

          {/* RIGHT — TDS & approval status */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {/* TDS (Sec 194H) — the checkbox is the admin's explicit choice; the
                ₹15,000 aggregate threshold below is only advisory (mandatory
                vs optional), it never overrides that choice back to zero. */}
            <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16, flex:1 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={form.deduct_tds} onChange={e=>setForm(f=>({...f,deduct_tds:e.target.checked}))} />
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Deduct TDS on this credit</span>
              </label>
              {selectedPartner && (
                <p style={{ fontSize:11, color:T.text3, margin:'8px 0 0', lineHeight:1.6 }}>
                  FY commission credited so far: <strong>{fmt(fyCredited)}</strong> · Threshold ₹15,000 (Sec 194H)
                  {!selectedPartner.pan && <span style={{ color:'#dc2626' }}> · No PAN on file — consider 20% (Sec 206AA)</span>}
                </p>
              )}
              {form.deduct_tds ? (
                <>
                  {!wouldCrossThreshold && (
                    <p style={{ fontSize:11, color:'#16a34a', margin:'10px 0 0' }}>Aggregate stays within ₹15,000 for the FY — TDS is optional here, not mandatory.</p>
                  )}
                  <div style={{ display:'grid', gridTemplateColumns:'1.3fr 1fr', gap:10, marginTop:12 }}>
                    <select value={form.tds_section} onChange={e=>setForm(f=>({...f,tds_section:e.target.value}))} style={is}>
                      <option value="194H">194H — Commission/Brokerage</option>
                      <option value="194J">194J — Professional Fees</option>
                      <option value="194C">194C — Contractor Payment</option>
                    </select>
                    <input type="number" min={0} step="0.01" value={form.tds_rate} onChange={e=>setForm(f=>({...f,tds_rate:e.target.value}))} placeholder="Rate %" style={is} />
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginTop:14, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                    <span style={{ color:T.text2 }}>TDS: <strong style={{ color:'#dc2626' }}>−{fmt(tdsPreview)}</strong></span>
                    <span style={{ color:T.text2 }}>Net to credit: <strong style={{ color:'#16a34a' }}>{fmt(netPreview)}</strong></span>
                  </div>
                </>
              ) : (
                <div style={{ fontSize:13, color:T.text2, marginTop:12, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                  Full amount to credit: <strong style={{ color:'#16a34a' }}>{fmt(amount)}</strong>
                </div>
              )}
            </div>

            <div style={{ background:darkMode?'rgba(139,92,246,0.1)':'#f5f3ff', border:`1px solid ${darkMode?'rgba(139,92,246,0.25)':'#ddd6fe'}`, borderRadius:10, padding:'10px 14px' }}>
              <p style={{ fontSize:12, color:'#7c3aed', fontWeight:600, margin:0 }}>⚠ Pending approval — Admin must approve before credits are added. TDS can be adjusted again at approval time.</p>
            </div>
          </div>
        </div>

        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving||!form.partner_id||!form.commission_amount||!form.reason}
            style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!form.partner_id||!form.commission_amount||!form.reason)?0.6:1 }}>
            {saving?'Submitting...':'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Partner Modal ───────────────────────────────────────────────────────
function EditPartnerModal({ partner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const [form, setForm] = useState({
    name: partner.name || '',
    phone: partner.phone || '',
    email: partner.email || '',
    pan: partner.pan || '',
    commission_type: partner.commission_type || 'percentage',
    commission_value: partner.commission_value || '',
    status: partner.status || 'active',
    notes: partner.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}`, {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) { showToast('Partner updated', 'success'); onSuccess(); }
      else { const d = await res.json().catch(()=>({})); showToast(d.message || 'Could not update partner', 'error'); }
    } catch (_) { showToast('Server error while updating partner', 'error'); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Edit Partner</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{partner.referral_partner_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Name</label>
              <input type="text" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Phone</label>
              <input type="text" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={is} />
            </div>
            {partner.client_id ? (
              <>
                {/* Client partner: PAN + email come only from the client profile — no second copy to edit. */}
                {[['Email', partner.email], ['PAN', partner.pan]].map(([label, value]) => (
                  <div key={label}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>{label} · from client</label>
                    <div style={{ ...is, background:T.bg3, color:value ? T.text : '#dc2626', fontWeight:value ? 600 : 500, cursor:'not-allowed', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {value || 'Not in client profile'}
                    </div>
                  </div>
                ))}
                <p style={{ gridColumn:'span 2', fontSize:11, color:T.text3, margin:'-4px 0 0' }}>
                  PAN and email are taken from the client profile{partner.client_name ? ` (${partner.client_name})` : ''}. To change them, edit the client — the partner updates automatically.
                </p>
              </>
            ) : (
              <>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Email</label>
                  <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>PAN</label>
                  <input type="text" value={form.pan} onChange={e=>setForm(f=>({...f,pan:e.target.value.toUpperCase()}))} placeholder="For TDS certificate" style={is} />
                </div>
              </>
            )}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Commission Type</label>
              <select value={form.commission_type} onChange={e=>setForm(f=>({...f,commission_type:e.target.value}))} style={is}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>
                {form.commission_type==='percentage'?'Rate (%)':'Amount (₹)'}
              </label>
              <input type="number" value={form.commission_value} onChange={e=>setForm(f=>({...f,commission_value:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Status</label>
              <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={is}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Notes</label>
              <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={is} />
            </div>
          </div>
        </div>
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
            {saving?'Saving...':'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Convert External Partner to Client Modal ─────────────────────────────────
function ConvertToClientModal({ partner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleConvert = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/convert-to-client`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ partner_id: partner.id }),
      });
      if (res.ok) { setDone(true); setTimeout(onSuccess, 1500); }
      else { const d = await res.json(); showToast(d.message || 'Conversion failed', 'error'); }
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#16a34a,#15803d)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Convert to Client</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>{partner.name} · {partner.referral_partner_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'24px', background:T.bg3 }}>
          {done ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:'#f0fdf4', border:'2px solid #bbf7d0', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Converted Successfully!</p>
              <p style={{ fontSize:12, color:T.text3, marginTop:6 }}>{partner.name} is now a client. Referral credits preserved.</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ background:darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'}`, borderRadius:12, padding:16 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 10px' }}>What happens when you convert:</p>
                {[
                  'A new Client profile is created with their name, phone, email',
                  'Partner type changes from External → Client Partner',
                  'All referral credits and commission history are preserved',
                  'They can now redeem credits against their own invoices',
                  'Their Referral Partner ID stays the same',
                ].map((item, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ flexShrink:0, marginTop:1 }}><polyline points="20 6 9 17 4 12"/></svg>
                    <p style={{ fontSize:12, color:T.text2, margin:0 }}>{item}</p>
                  </div>
                ))}
              </div>
              <div style={{ background:darkMode?'rgba(245,158,11,0.1)':'#fffbeb', border:`1px solid ${darkMode?'rgba(245,158,11,0.25)':'#fde68a'}`, borderRadius:10, padding:'10px 14px' }}>
                <p style={{ fontSize:12, color:'#d97706', margin:0, fontWeight:600 }}>⚠ You will need to complete the client profile (PAN, GSTIN etc.) in Clients module after conversion.</p>
              </div>
            </div>
          )}
        </div>
        {!done && (
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
            <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleConvert} disabled={saving} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving?'Converting...':'Confirm Convert'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralPartnersInner({ darkMode = false, T: theme }) {
  const T = theme || { bg: '#f4f7ff', bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', border2: '#e2e8f0', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBorder: '#f1f5f9', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  const { showToast, showConfirm } = useToast();

  const [activeTab, setActiveTab] = useState('client');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewPartner, setViewPartner] = useState(null);
  const [redeemPartner, setRedeemPartner] = useState(null);
  const [encashPartner, setEncashPartner] = useState(null);
  const [detailsPartner, setDetailsPartner] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [editPartner, setEditPartner] = useState(null);
  const [convertPartner, setConvertPartner] = useState(null);
  const [bankPartner, setBankPartner] = useState(null);
  const [payoutRequests, setPayoutRequests] = useState([]);

  const fetchPayoutRequests = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/payout-requests/list`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPayoutRequests(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [token]);
  useEffect(() => { fetchPayoutRequests(); }, [fetchPayoutRequests]);

  const handleDeletePartner = async (p) => {
    if (Number(p.total_earned || 0) > 0) {
      showToast(`Cannot delete — ${p.name} has earned ₹${Number(p.total_earned).toLocaleString('en-IN')} in commissions. Deactivate instead.`, 'error');
      return;
    }
    if (!(await showConfirm(`Delete ${p.name}? This cannot be undone.`, { title: 'Delete Partner', confirmLabel: 'Delete', confirmColor: '#ef4444' }))) return;
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${p.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { showToast(`${p.name} deleted`, 'success'); fetchPartners(); }
      else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not delete partner', 'error'); }
    } catch (_) { showToast('Server error while deleting partner', 'error'); }
  };
  const [commissions, setCommissions] = useState([]);
  const [showCommissionModal, setShowCommissionModal] = useState(null);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCommissions(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [token]);

  useEffect(() => { fetchCommissions(); }, [fetchCommissions]);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners`, { headers: h });
      const data = await res.json();
      setPartners(Array.isArray(data) ? data : []);
    } catch (_) { setPartners([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const clientPartners = partners.filter(p => p.partner_type === 'client');
  const externalPartners = partners.filter(p => p.partner_type === 'external');

  const filtered = (activeTab === 'client' ? clientPartners : activeTab === 'external' ? externalPartners : partners)
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.referral_partner_id?.toLowerCase().includes(search.toLowerCase()));

  const pendingCount = commissions.filter(c => c.status === 'pending' && !c.approval_blocker).length;
  const pendingPayouts = payoutRequests.filter(r => r.status === 'pending').length;
  const TABS = [
    { id: 'client', label: `Client Partners (${clientPartners.length})` },
    // Partners are created from a client (Referral toggle); this tab only lists legacy external partners.
    ...(externalPartners.length ? [{ id: 'external', label: `External Partners (${externalPartners.length})` }] : []),
    { id: 'commissions', label: `Commissions${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
    { id: 'payouts', label: `Payout Requests${pendingPayouts > 0 ? ` (${pendingPayouts})` : ''}` },
    { id: 'performance', label: 'Performance' },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', padding: 24, background: darkMode ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e' : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: 0 }}>Referral Partners</h2>
          <p style={{ fontSize: 12, color: T.text3, margin: '4px 0 0' }}>{partners.length} partners · {fmt(partners.reduce((s, p) => s + (Number(p.credit_balance) || 0), 0))} total credits pending</p>
        </div>
      </div>

      {/* Tabs + Search */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0, gap: 14 }}>
        <div style={{ display: 'flex', gap: 4, background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 5, boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 1px 4px rgba(15,31,69,0.06)' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                background: activeTab === tab.id ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
                color: activeTab === tab.id ? '#fff' : T.text3,
                boxShadow: activeTab === tab.id ? '0 2px 8px rgba(15,31,69,0.25)' : 'none',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab !== 'performance' && activeTab !== 'commissions' && activeTab !== 'payouts' && (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {/* List/Grid toggle */}
            <div style={{ display:'flex', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:9, padding:3 }}>
              <button onClick={() => setViewMode('list')} title="List View"
                style={{ width:30, height:26, borderRadius:7, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                  background: viewMode==='list'?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'transparent',
                  color: viewMode==='list'?'#fff':T.text3 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
              <button onClick={() => setViewMode('grid')} title="Grid View"
                style={{ width:30, height:26, borderRadius:7, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                  background: viewMode==='grid'?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'transparent',
                  color: viewMode==='grid'?'#fff':T.text3 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              </button>
            </div>
          </div>
        )}
        {activeTab !== 'performance' && activeTab !== 'commissions' && activeTab !== 'payouts' && (
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search partners..." style={{ width: '100%', border: `1px solid ${T.border2}`, borderRadius: 10, padding: '9px 12px 9px 32px', fontSize: 13, outline: 'none', background: T.bg2, color: T.text, boxSizing: 'border-box' }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
        {activeTab === 'performance' ? (
          <PerformanceTab partners={partners} darkMode={darkMode} T={T} />
        ) : activeTab === 'payouts' ? (
          <PayoutRequestsTab requests={payoutRequests} onRefresh={() => { fetchPayoutRequests(); fetchPartners(); }} darkMode={darkMode} T={T} token={token} />
        ) : activeTab === 'commissions' ? (
          <CommissionsTab
            commissions={commissions}
            partners={partners}
            onRefresh={() => { fetchCommissions(); fetchPartners(); }}
            onManualAdd={(p) => setShowCommissionModal(p)}
            darkMode={darkMode}
            T={T}
            token={token}
          />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.text3, fontSize: 13 }}>Loading partners...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: T.bg2, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: 0 }}>
              {activeTab === 'client' ? 'No client partners yet' : 'No external partners yet'}
            </p>
            <p style={{ fontSize: 12, color: T.text3, marginTop: 6 }}>
              {activeTab === 'client' ? 'Open a client and turn on Referral Partner (PAN and email are required) to add them here' : 'No external partners'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {filtered.map(partner => (
              <PartnerCard key={partner.id} partner={partner} onView={setViewPartner} onDetails={setDetailsPartner} onRedeem={setRedeemPartner} onEncash={setEncashPartner} darkMode={darkMode} T={T} />
            ))}
          </div>
        ) : (
          /* LIST VIEW */
          <div style={{ background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow:darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['Partner','Type','Commission','Ledger Balance','Credits Earned','Total Earned','Leads','Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding:'48px 16px', textAlign:'center', color:T.text3, fontSize:13 }}>No partners found</td></tr>
                ) : filtered.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}`, background:darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:9, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
                          {(p.name||'P').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <button onClick={()=>setDetailsPartner(p)} title="View partner details"
                            style={{ fontSize:13, fontWeight:600, color:'#2563eb', margin:0, padding:0, border:'none', background:'none', cursor:'pointer', textAlign:'left' }}
                            onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
                            onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>{p.name}</button>
                          {!p.pan && <span title="PAN missing" style={{ marginLeft:6, fontSize:10, fontWeight:700, color:'#dc2626' }}>PAN missing</span>}
                          <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{p.referral_partner_id}</p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <Badge label={p.partner_type==='client'?'Client':'External'} color={p.partner_type==='client'?'#3b82f6':'#8b5cf6'} bg={p.partner_type==='client'?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode?'rgba(139,92,246,0.12)':'#f5f3ff')} />
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2 }}>
                      {p.commission_type==='percentage'?`${Number(p.commission_value||0)}%`:fmt(p.commission_value)}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:(p.ledger_outstanding||0)>0?'#dc2626':'#16a34a' }}>
                      {p.partner_type==='client'?fmt(p.ledger_outstanding||0):'—'}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:700, color:'#16a34a' }}>
                      {fmt(p.credit_balance||0)}
                      {Number(p.payout?.on_hold||0) > 0 && (
                        <p style={{ fontSize:10, fontWeight:500, color:'#a16207', margin:'2px 0 0' }} title="Invoice not fully paid or work not completed yet">
                          {fmt(p.payout.on_hold)} on hold
                        </p>
                      )}
                      {p.pending_payout_requests > 0 && (
                        <p style={{ fontSize:10, fontWeight:600, color:'#3b82f6', margin:'2px 0 0', cursor:'pointer' }} onClick={() => setActiveTab('payouts')}>
                          {p.pending_payout_requests} payout request{p.pending_payout_requests > 1 ? 's' : ''}
                        </p>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:'#3b82f6' }}>{fmt(p.total_earned||0)}</td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:T.text2 }}>
                      <span style={{ fontWeight:600, color:'#8b5cf6' }}>{fmtN(p.total_leads||0)}</span>
                      <span style={{ fontSize:11, color:T.text3 }}> / {fmtN(p.converted_leads||0)} conv.</span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        <button onClick={()=>setViewPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12, fontWeight:500, cursor:'pointer' }}
                          onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Leads</button>
                        {isAdminUser() && p.partner_type==='client' && (p.credit_balance||0)>0 && (
                          <button onClick={()=>setRedeemPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:12, fontWeight:500, cursor:'pointer' }}>Redeem</button>
                        )}
                        {isAdminUser() && (p.credit_balance||0)>0 && (
                          <button onClick={()=>setEncashPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', fontSize:12, fontWeight:500, cursor:'pointer' }}>Encash</button>
                        )}
                        {/* Bank account on file */}
                        <button onClick={()=>setBankPartner(p)} title={p.bank_account_number ? (p.bank_verified ? 'Bank account (verified)' : 'Bank account (not verified)') : 'Add bank account'}
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${p.bank_account_number && !p.bank_verified ? '#fde047' : T.border2}`, background:'none', color:p.bank_verified ? '#16a34a' : T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h18"/><path d="M5 10v8"/><path d="M19 10v8"/><path d="M12 10v8"/><path d="M3 21h18"/><path d="M12 3l9 5H3z"/></svg>
                        </button>
                        {/* Edit */}
                        {isAdminUser() && <button onClick={()=>setEditPartner(p)} title="Edit Partner"
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#f97316';e.currentTarget.style.borderColor='#f97316';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>}
                        {/* Convert to Client — only for external */}
                        {p.partner_type==='external' && (
                          <button onClick={()=>setConvertPartner(p)} title="Convert to Client"
                            style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#16a34a';e.currentTarget.style.borderColor='#16a34a';}}
                            onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                          </button>
                        )}
                        {/* Delete */}
                        {isAdminUser() && <button onClick={()=>handleDeletePartner(p)} title={Number(p.total_earned||0)>0?'Cannot delete — has commission history':'Delete Partner'}
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:Number(p.total_earned||0)>0?T.text3:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:Number(p.total_earned||0)>0?'not-allowed':'pointer', opacity:Number(p.total_earned||0)>0?0.4:1 }}
                          onMouseEnter={e=>{ if(Number(p.total_earned||0)===0){ e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; e.currentTarget.style.borderColor='#ef4444'; }}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {viewPartner && <PartnerLeadsModal partner={viewPartner} onClose={() => setViewPartner(null)} darkMode={darkMode} T={T} />}
      {redeemPartner && <RedeemModal partner={redeemPartner} onClose={() => setRedeemPartner(null)} onSuccess={fetchPartners} darkMode={darkMode} T={T} />}
      {encashPartner && <EncashModal partner={encashPartner} onClose={() => setEncashPartner(null)} onSuccess={() => { fetchPartners(); fetchPayoutRequests(); }} darkMode={darkMode} T={T} />}
      {bankPartner && <BankModal partner={bankPartner} onClose={() => setBankPartner(null)} onSuccess={fetchPartners} darkMode={darkMode} T={T} />}
      {detailsPartner && (
        <PartnerDetailsModal partner={detailsPartner} onClose={() => setDetailsPartner(null)} onChanged={fetchPartners}
          onEdit={() => { setEditPartner(detailsPartner); setDetailsPartner(null); }}
          onLeads={() => { setViewPartner(detailsPartner); setDetailsPartner(null); }}
          darkMode={darkMode} T={T} />
      )}
      {editPartner && <EditPartnerModal partner={editPartner} onClose={() => setEditPartner(null)} onSuccess={() => { setEditPartner(null); fetchPartners(); }} darkMode={darkMode} T={T} token={token} />}
      {convertPartner && <ConvertToClientModal partner={convertPartner} onClose={() => setConvertPartner(null)} onSuccess={() => { setConvertPartner(null); fetchPartners(); }} darkMode={darkMode} T={T} token={token} />}
      {showCommissionModal && (
        <ManualCommissionModal
          partner={showCommissionModal}
          onClose={() => setShowCommissionModal(null)}
          onSuccess={() => { fetchCommissions(); fetchPartners(); setShowCommissionModal(null); }}
          darkMode={darkMode}
          T={T}
          token={token}
        />
      )}
    </div>
  );
}

export default function ReferralPartners(props) {
  return (
    <ToastProvider>
      <ReferralPartnersInner {...props} />
    </ToastProvider>
  );
}
